import { lockBodyScroll } from "../utils/scrollLock";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IoClose, IoChevronBack, IoChevronForward } from "react-icons/io5";
import { useFocusTrap } from "../utils/focusTrap";

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const SWIPE_THRESHOLD = 60;

function distanceBetween(touches) {
  const [a, b] = touches;
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

// Full-screen photo viewer with real two-finger pinch-to-zoom (and drag to
// pan once zoomed in). The browser's own pinch-zoom is turned off on this
// image specifically (touchAction: "none" below) so it doesn't fight this
// component's own pinch/pan gesture handling. This works independently of
// the page-level viewport zoom setting (public/index.html) — raw touch
// events still reach this component's own handlers either way.
// When given more than one image, a one-finger horizontal swipe (only while
// not zoomed in) moves between photos, same gesture as the feed carousel.
export default function ImageLightbox({ images, initialIndex = 0, alt, onClose }) {
  const photos = images && images.length > 0 ? images : [];
  const clampedInitial = Math.min(Math.max(initialIndex, 0), Math.max(photos.length - 1, 0));

  const [index, setIndex] = useState(clampedInitial);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isGesturing, setIsGesturing] = useState(false);

  // Raw gesture tracking that shouldn't itself trigger re-renders
  const gesture = useRef({
    pinchStartDistance: 0,
    pinchStartScale: 1,
    panStart: null,
    panStartTranslate: { x: 0, y: 0 },
    swiping: false,
    swipeStartX: 0,
  });
  const backdropRef = useRef(null);
  useFocusTrap(backdropRef, true);

  // Every other overlay in the app (FollowListModal, ReportModal,
  // ImageCropModal) locks background scroll and closes on Escape — this one
  // was missing both.
  useEffect(() => {
    const unlockScroll = lockBodyScroll();

    function onKeyDown(e) {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft" && photos.length > 1) goTo(index - 1);
      else if (e.key === "ArrowRight" && photos.length > 1) goTo(index + 1);
    }
    window.addEventListener("keydown", onKeyDown);

    return () => {
      unlockScroll();
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, index, photos.length]);

  if (photos.length === 0) return null;

  function clampScale(value) {
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));
  }

  function resetZoom() {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }

  function goTo(nextIndex) {
    resetZoom();
    setIndex(Math.min(Math.max(nextIndex, 0), photos.length - 1));
  }

  function handleTouchStart(e) {
    if (e.touches.length === 2) {
      setIsGesturing(true);
      gesture.current.pinchStartDistance = distanceBetween(e.touches);
      gesture.current.pinchStartScale = scale;
    } else if (e.touches.length === 1 && scale > 1) {
      setIsGesturing(true);
      const t = e.touches[0];
      gesture.current.panStart = { x: t.clientX, y: t.clientY };
      gesture.current.panStartTranslate = translate;
    } else if (e.touches.length === 1 && scale === 1 && photos.length > 1) {
      gesture.current.swiping = true;
      gesture.current.swipeStartX = e.touches[0].clientX;
    }
  }

  function handleTouchMove(e) {
    if (e.touches.length === 2 && gesture.current.pinchStartDistance) {
      e.preventDefault();
      const newDistance = distanceBetween(e.touches);
      const ratio = newDistance / gesture.current.pinchStartDistance;
      setScale(clampScale(gesture.current.pinchStartScale * ratio));
    } else if (e.touches.length === 1 && gesture.current.panStart) {
      e.preventDefault();
      const t = e.touches[0];
      setTranslate({
        x: gesture.current.panStartTranslate.x + (t.clientX - gesture.current.panStart.x),
        y: gesture.current.panStartTranslate.y + (t.clientY - gesture.current.panStart.y),
      });
    } else if (e.touches.length === 1 && gesture.current.swiping) {
      setSwipeOffset(e.touches[0].clientX - gesture.current.swipeStartX);
    } else if (e.touches.length === 1) {
      // Lifting one finger out of a two-finger pinch lands here with neither
      // panStart nor swiping set (those are only armed in handleTouchStart) —
      // without this, the image just freezes until the last finger lifts
      // too, instead of continuing to track the finger still on screen.
      const t = e.touches[0];
      if (scale > 1) {
        setIsGesturing(true);
        gesture.current.panStart = { x: t.clientX, y: t.clientY };
        gesture.current.panStartTranslate = translate;
      } else if (photos.length > 1) {
        gesture.current.swiping = true;
        gesture.current.swipeStartX = t.clientX;
      }
    }
  }

  function handleTouchEnd(e) {
    if (e.touches.length === 0) {
      setIsGesturing(false);
      gesture.current.panStart = null;
      gesture.current.pinchStartDistance = 0;

      if (scale <= 1) resetZoom();

      if (gesture.current.swiping) {
        gesture.current.swiping = false;
        if (swipeOffset <= -SWIPE_THRESHOLD && index < photos.length - 1) {
          setIndex(index + 1);
        } else if (swipeOffset >= SWIPE_THRESHOLD && index > 0) {
          setIndex(index - 1);
        }
        setSwipeOffset(0);
      }
    }
  }

  // A call/notification interrupting the gesture fires touchcancel instead
  // of touchend — without handling it, isGesturing/swiping/panStart would
  // never reset, leaving the transition disabled and the next tap's gesture
  // state built on stale leftovers.
  function handleTouchCancel() {
    setIsGesturing(false);
    gesture.current.panStart = null;
    gesture.current.pinchStartDistance = 0;
    gesture.current.swiping = false;
    setSwipeOffset(0);
    if (scale <= 1) resetZoom();
  }

  function handleDoubleClick() {
    if (scale > 1) resetZoom();
    else setScale(2.2);
  }

  const src = photos[index];

  // Portaled — triggered from deep inside ImageCarousel, itself nested
  // inside feed cards on Home/PublicProfile/Trending/... An ancestor with a
  // CSS transform/filter (the carousel's own heart-burst animation, for one)
  // would otherwise hijack this backdrop's position:fixed to be relative to
  // that ancestor's box instead of the viewport — same class of bug just
  // fixed in Toast.jsx and ShareButton.jsx.
  return createPortal(
    <div style={backdrop} ref={backdropRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={alt || "Photo"} onClick={onClose}>
      <button style={closeBtn} onClick={onClose} aria-label="Close">
        <IoClose size={20} />
      </button>

      {photos.length > 1 && index > 0 && (
        <button
          style={{ ...navBtn, left: 14 }}
          onClick={e => {
            e.stopPropagation();
            goTo(index - 1);
          }}
          aria-label="Previous photo"
        >
          <IoChevronBack size={20} />
        </button>
      )}
      {photos.length > 1 && index < photos.length - 1 && (
        <button
          style={{ ...navBtn, right: 14 }}
          onClick={e => {
            e.stopPropagation();
            goTo(index + 1);
          }}
          aria-label="Next photo"
        >
          <IoChevronForward size={20} />
        </button>
      )}

      <img
        key={index}
        src={src}
        alt={alt || "Design"}
        draggable={false}
        style={{
          ...image,
          transform: `translate(${translate.x + swipeOffset}px, ${translate.y}px) scale(${scale})`,
          transition: isGesturing || gesture.current.swiping ? "none" : "transform 0.15s ease",
        }}
        onClick={e => e.stopPropagation()}
        onDoubleClick={handleDoubleClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
      />

      {photos.length > 1 && (
        <span style={counterBadge}>
          {index + 1}/{photos.length}
        </span>
      )}
    </div>,
    document.body
  );
}

const backdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(10, 7, 4, 0.92)",
  zIndex: 1300,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
};

const closeBtn = {
  position: "absolute",
  top: "calc(14px + env(safe-area-inset-top))",
  right: 14,
  width: 36,
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  border: "none",
  background: "rgba(255,255,255,0.15)",
  color: "var(--accent-contrast)",
  cursor: "pointer",
  zIndex: 1301,
};

const navBtn = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: 40,
  height: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  border: "none",
  background: "rgba(255,255,255,0.15)",
  color: "var(--accent-contrast)",
  cursor: "pointer",
  zIndex: 1301,
};

const counterBadge = {
  position: "absolute",
  bottom: "calc(18px + env(safe-area-inset-bottom))",
  left: "50%",
  transform: "translateX(-50%)",
  background: "rgba(255,255,255,0.15)",
  color: "var(--accent-contrast)",
  padding: "3px 11px",
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 700,
};

const image = {
  maxWidth: "92vw",
  maxHeight: "88vh",
  objectFit: "contain",
  touchAction: "none",
  cursor: "zoom-in",
};
